import { PagedRequestDto } from '../paged-request.model';
import { Location } from '../location.model';
import { FacilityClassification, FacilityType } from '../../enums';
import { ScopeOfPracticeEnum } from '@umss/shared/utils';

export interface FacilityDto {
  /**
   * format: int32
   */
  facilityTypeId?: number;

  facilityTypeName?: string;

  facilityTypeHasSubtypes: boolean;

  classification: FacilityClassification;

  classificationDisplay: string;

  status: FacilityStatus;

  statusDisplay: string;

  /**
   * Why the facility was suspended, when it is.
   *
   * Always absent today — see {@link SuspensionReason}. Typed here so the one
   * place that explains a suspension to its facility can name the cause the
   * moment the API sends it.
   */
  suspensionReason?: SuspensionReason;
  /**
   * format: uuid
   */
  corporationId?: string;

  corporationName?: string;

  billingManagedByCorporate: boolean;

  location: Location;

  contactPhone?: string;

  contactEmail?: string;

  websiteUrl?: string;

  onboardingCompleted: boolean;
  /**
   * format: date
   */
  contractSignedDate?: string;

  timeZone?: string;

  supportedScopes: ScopeOfPracticeEnum[];

  contacts: FacilityContactDto[];
  /**
   * format: double
   */
  distanceMiles?: number;

  isFavorite: boolean;
  /**
   * minLength: 0
   * maxLength: 255
   */
  name: string;
  /**
   * minLength: 0
   * maxLength: 1000
   */
  description?: string;

  isActive: boolean;
  /**
   * format: uuid
   */
  id: string;
  /**
   * format: date-time
   */
  createdAt: string;
  /**
   * format: date-time
   */
  updatedAt: string;
}

/**
 * The facility lifecycle, as the API names it.
 *
 * `PendingApproval` and `Rejected` are the API's own spellings — this used to
 * declare a `Pending` that the API never sends and omit `Rejected` entirely,
 * so a facility in either state fell through every branch that switched on it
 * and rendered its raw status string.
 */
export enum FacilityStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  PendingApproval = 'PendingApproval',
  Suspended = 'Suspended',
  Rejected = 'Rejected'
}

/**
 * How each status is written for a reader. The API's names are PascalCase
 * identifiers, so anything rendering `status` straight out of the payload shows
 * "PendingApproval"; this is the one place that spelling is decided.
 */
export const FACILITY_STATUS_LABELS: Record<FacilityStatus, string> = {
  [FacilityStatus.Active]: 'Active',
  [FacilityStatus.Inactive]: 'Inactive',
  [FacilityStatus.PendingApproval]: 'Pending approval',
  [FacilityStatus.Suspended]: 'Suspended',
  [FacilityStatus.Rejected]: 'Rejected'
};

/**
 * Why a facility was suspended. Each is undone differently, so it decides what
 * a suspended facility is told to do about it.
 *
 * TODO(BE): not on `FacilityDto` yet — the API carries it only on
 * `GET /api/facilities/suspended`, which is SuperAdmin-only, so a facility
 * cannot read the reason for its own suspension. Until it lands, anything
 * reading this falls back to reason-neutral copy.
 */
export enum SuspensionReason {
  None = 'None',
  Citations = 'Citations',
  Manual = 'Manual',
  UnpaidInvoice = 'UnpaidInvoice',
  LateCancellation = 'LateCancellation',
  /** The parent corporation stopped paying; the facility must add its own method. */
  CorporatePayerRemoved = 'CorporatePayerRemoved'
}

export interface FacilityContactDto {
  id: number;
  contactType: string;
  firstName: string;
  lastName: string;
  fullName: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary: boolean;
}

export interface FacilitySearchDto extends PagedRequestDto {
  // Filtros específicos de Facility
  classification?: FacilityClassification | null;
  isActive?: boolean | null; // Filter by active status. Defaults to true (only active facilities). Set to false for inactive only, or null for all facilities
  facilityTypeId?: number | null;
  stateCode?: string | null;
  latitude?: number | null; // decimal
  longitude?: number | null; // decimal
  radiusMiles?: number | null; // double
  corporateId?: string | null;
  status?: FacilityStatus | null;
}

export interface CreateFacilityDto extends CreateNamedEntityDto {
  isActive?: boolean; // Default true
  facilityTypeId: FacilityType;
  classification?: FacilityClassification; // Required
  location?: CreateLocationDto | null;
  contactPhone?: string | null; // Phone validation, max 20 characters
  contactEmail?: string | null; // Email validation, max 255 characters
  websiteUrl?: string | null; // URL validation
  contractSignedDate?: string | null; // DateOnly as ISO string (YYYY-MM-DD)
  supportedScopes?: ScopeOfPracticeEnum[]; // Default empty array
}

export interface CreateNamedEntityDto {
  name: string; // Required, max 255 characters
  description?: string | null; // Optional, max 1000 characters
}

export interface CreateLocationDto {
  addressLine1: string; // Required, max 255 characters
  addressLine2?: string | null; // Optional, max 255 characters
  city: string; // Required, max 100 characters
  stateCode: string; // Required, max 2 characters
  zipCode: string; // Required, max 10 characters
}

export interface UpdateFacilityDto extends CreateNamedEntityDto {
  facilityTypeId?: FacilityType; // Required
  classification: FacilityClassification;
  status?: FacilityStatus; // Required
  location?: CreateLocationDto | null;
  contactPhone?: string | null; // Phone validation, max 20 characters
  contactEmail?: string | null; // Email validation, max 255 characters
  websiteUrl?: string | null; // URL validation
  isActive?: boolean; // Default true
  supportedScopes?: ScopeOfPracticeEnum[]; // Default empty array
}
